import { useState, useEffect, useRef } from 'react';
import { portalGetMessages, portalSendMessage } from '../services/api';
import { ChatCircleDots, PaperPlaneTilt, User } from '@phosphor-icons/react';

export default function TenantMessages() {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const res = await portalGetMessages();
      setMessages(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await portalSendMessage(content.trim());
      setContent('');
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6 max-w-3xl flex flex-col h-[calc(100vh-8rem)]" data-testid="tenant-messages-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          Nachrichten
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Direkt mit Ihrem Vermieter kommunizieren</p>
      </div>

      <div className="bg-white border border-gray-200 flex-1 flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
          {loading ? (
            <div className="text-center text-slate-400">Lädt...</div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-slate-400">
              <ChatCircleDots weight="light" className="w-16 h-16 mb-3" />
              <p>Noch keine Nachrichten</p>
              <p className="text-sm mt-1">Schreiben Sie Ihrem Vermieter eine Nachricht.</p>
            </div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.sender_role === 'tenant' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${m.id}`}
              >
                <div className={`max-w-xs sm:max-w-md ${m.sender_role === 'tenant' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-900'} px-4 py-2.5 rounded-lg`}>
                  <p className="text-xs font-medium opacity-75 mb-1">{m.sender_name}</p>
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  <p className="text-[10px] opacity-60 mt-1 text-right">{formatTime(m.created_at)}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={handleSend} className="border-t border-gray-200 p-3 flex gap-2">
          <input
            type="text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Nachricht schreiben..."
            className="flex-1 border border-gray-300 px-4 py-2.5 focus:border-emerald-600 outline-none"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={sending || !content.trim()}
            className="bg-emerald-700 text-white px-4 py-2.5 hover:bg-emerald-800 disabled:opacity-50 flex items-center gap-2"
            data-testid="message-send-button"
          >
            <PaperPlaneTilt weight="bold" className="w-4 h-4" />
            <span className="hidden sm:inline">Senden</span>
          </button>
        </form>
      </div>
    </div>
  );
}
